import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Grow from "@mui/material/Grow";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import TextField from "@mui/material/TextField";
import { labelWithOptionalRequiredIcon } from "ds_legacy/components/RequiredIcon";
import { BORDER_BOTTOM } from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { border, dp, padding } from "ds_legacy/materials/metrics";
import { Body, FONT_SIZE_16 } from "ds_legacy/materials/typography";
import { MenuItem } from "dsl/atoms/MenuItem";
import { UndoIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useAutocomplete, useInputCursorPos } from "./hooks";
import { AutocompleteModal } from "./modals";
import { FormValue, Requirement, Suggestion } from "./types";
import { FullWidth, languageTagMap } from "./utils";

const AutocompleteContainer = styled.div`
  z-index: 100;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  background-color: white;
  border: ${border({ color: BORDER_BOTTOM })};
`;

const Anchor = styled.div`
  position: relative;
  top: ${dp(-10)};
`;

function Autocomplete({
  complete,
  cursorPosition,
  description,
  name,
  requirements,
  setValue,
  value,
}: {
  complete: (arg: Array<string>) => string;
  cursorPosition: number;
  description: string;
  name: string;
  requirements: Requirement[];
  setValue: (v: string) => void;
  value: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <MenuItem
        sx={{ maxWidth: "100%", overflow: "hidden", padding: padding(1, 2) }}
        onClick={() => setOpen(true)}
      >
        <Body style={{ textOverflow: "ellipsis" }}>
          <b>{name}</b>
          <br />
          <i>{description}</i>
        </Body>
      </MenuItem>
      <AutocompleteModal
        open={open}
        setOpen={setOpen}
        suggestion={{ complete, requirements, name, description }}
        val={value}
        setVal={setValue}
        stringStart={cursorPosition}
      />
    </>
  );
}

export const InputField = ({
  autoFocus = false,
  error,
  helperText,
  id,
  label,
  onReset,
  placeholder,
  required = false,
  rows = "3",
  setTags,
  setValue,
  suggestions,
  tabIndex,
  tags,
  value,
}: {
  autoFocus?: boolean;
  error: string | null | undefined;
  helperText?: string;
  id?: string;
  label: string;
  onReset?: () => void;
  placeholder?: string;
  required?: boolean;
  rows?: string;
  setTags?: (tags: Array<number>) => void;
  setValue: (v: FormValue) => void;
  suggestions?: Array<Suggestion>;
  tabIndex?: number;
  tags?: Array<number>;
  value: FormValue;
}) => {
  const ref = useRef();
  const inputId = label.replace(" ", "");
  const cursorPos = useInputCursorPos(inputId);
  const autocomplete = useAutocomplete(value, cursorPos, suggestions);
  const isMultiline = useMemo(() => parseInt(rows) > 1, [rows]);
  const variant = useMemo(
    () => (isMultiline ? "outlined" : "standard"),
    [isMultiline],
  );
  const disabled = useMemo(() => {
    if (!id) return false;

    return tags?.some((tag) => [languageTagMap[id]?.ignore].includes(tag));
  }, [tags, id]);

  useEffect(() => {
    if (disabled && value !== "") setValue("");
  }, [disabled, value, setValue]);

  return (
    <FullWidth style={{ position: "relative" }}>
      <FormControl error={error != null} fullWidth>
        <HorizontalLayout>
          <VerticalLayout stretched>
            <TextField
              helperText={!error && helperText}
              id={inputId}
              disabled={disabled}
              autoFocus={autoFocus}
              label={labelWithOptionalRequiredIcon({ label, required })}
              variant={variant as ToType}
              multiline={isMultiline}
              rows={rows}
              margin="normal"
              error={error != null}
              value={value || ""}
              onChange={(e) => setValue(e.target.value)}
              fullWidth
              placeholder={placeholder}
              InputProps={{
                inputProps: { tabIndex },
                endAdornment: onReset && !disabled && (
                  <InputAdornment position="end">
                    <IconButton size="large" onClick={onReset}>
                      <UndoIcon size={FONT_SIZE_16} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Anchor ref={ref as ToType} />
            <FormHelperText>{error != null && error}</FormHelperText>
          </VerticalLayout>
          {tags != null &&
            id != null &&
            languageTagMap[id] != null &&
            setTags != null && (
              <VerticalLayout style={{ padding: padding(0.5) }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tags.includes(languageTagMap[id].ignore)}
                      onChange={() =>
                        tags.includes(languageTagMap[id].ignore)
                          ? setTags(
                              tags.filter(
                                (tag) => tag !== languageTagMap[id].ignore,
                              ),
                            )
                          : setTags(tags.concat(languageTagMap[id].ignore))
                      }
                      value={`${id}_ignore`}
                      color="primary"
                    />
                  }
                  label="Ignore"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tags.includes(languageTagMap[id].todo)}
                      onChange={() =>
                        tags.includes(languageTagMap[id].todo)
                          ? setTags(
                              tags.filter(
                                (tag) => tag !== languageTagMap[id].todo,
                              ),
                            )
                          : setTags(tags.concat(languageTagMap[id].todo))
                      }
                      value={`${id}_todo`}
                      color="primary"
                    />
                  }
                  label="To do"
                />
              </VerticalLayout>
            )}
        </HorizontalLayout>
      </FormControl>
      {typeof value == "string" && (
        <Popper
          open={autocomplete.length > 0}
          anchorEl={ref.current}
          transition
          style={{ width: "40vw" }}
        >
          {({ placement, TransitionProps }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "center top" : "center bottom",
              }}
            >
              <Paper>
                <AutocompleteContainer>
                  <MenuList>
                    {autocomplete.map((suggestion) => (
                      <Autocomplete
                        key={suggestion.name}
                        {...suggestion}
                        value={value}
                        setValue={setValue}
                        cursorPosition={cursorPos}
                      />
                    ))}
                  </MenuList>
                </AutocompleteContainer>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </FullWidth>
  );
};
